import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaClipboard } from 'react-icons/fa';
import { aiService } from '../../services/aiService';
import { useNotification } from '../../contexts/NotificationContext';
import { createNewChat, saveMessage, getChatMessages } from '../../services/dataService';
import { CustomerTargetGroup, Domain } from '../../types/data';
import { useChats } from '../../contexts/ChatsContext';
import { extractErrorMessageFromResponse } from '../../services/errorService';
import * as Sentry from '@sentry/react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Function to strip markdown from text
const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s+/g, '') // Remove headings
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.*?)\*/g, '$1') // Remove italic
    .replace(/~~(.*?)~~/g, '$1') // Remove strikethrough
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1') // Replace links with just the text
    .replace(/`{3}(?:.*?)\n([\s\S]*?)`{3}/g, '$1') // Remove code blocks
    .replace(/`(.*?)`/g, '$1') // Remove inline code
    .replace(/>\s+(.*)/g, '$1') // Remove blockquotes
    .replace(/- (.*)/g, '$1') // Remove list markers
    .replace(/\d+\. (.*)/g, '$1') // Remove numbered list markers
    .replace(/\n{2,}/g, '\n\n'); // Normalize multiple new lines
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatWindowProps {
  activeDomain: Domain | null;
  selectedCustomerGroup: string;
  customerTargetGroups: CustomerTargetGroup[];
  targetCompany: string | null;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  activeDomain,
  selectedCustomerGroup,
  customerTargetGroups,
  targetCompany,
}: ChatWindowProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isNewChat, setIsNewChat] = useState(false);
  const { t } = useTranslation();
  const { currentChatId, refreshChats, handleChatSelect } = useChats();
  const { showNotification } = useNotification();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      const { scrollHeight, clientHeight, scrollTop } = chatContainer;
      const isScrolledNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isScrolledNearBottom) {
        chatContainer.scrollTop = scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    const loadChatMessages = async () => {
      if (!currentChatId) {
        return;
      }

      try {
        const chatMessages = await getChatMessages(currentChatId);
        if (!isNewChat) {
          setMessages(chatMessages);
        }
      } catch (error) {
        console.error('Error loading chat messages:', error);
        showNotification('Error loading chat messages', 'error');
      }
    };

    loadChatMessages();
  }, [currentChatId]);

  useEffect(() => {
    if (!currentChatId) {
      setMessages([]);
      setInputMessage('');
    }
  }, [currentChatId]);

  const initializeChat = async (): Promise<string | null> => {
    if (!activeDomain || !selectedCustomerGroup || !targetCompany) return null;

    const customerGroup = customerTargetGroups.find(
      (group) => group.name === selectedCustomerGroup
    );
    if (!customerGroup) return null;

    try {
      const chat = await createNewChat(
        `Chat ${Math.random().toString(36).substring(2, 15)}`,
        customerGroup.id,
        targetCompany,
        activeDomain.id
      );
      setIsNewChat(true);
      handleChatSelect(chat.id);
      setTimeout(() => {
        setIsNewChat(false);
      }, 3000);
      return chat.id;
    } catch (error) {
      console.error('Error creating chat:', error);
      showNotification('Error creating chat session', 'error');
      return null;
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !activeDomain || !selectedCustomerGroup) {
      showNotification('Please select a customer group and enter a message', 'error');
      return;
    }

    const newMessage: ChatMessage = {
      role: 'user',
      content: inputMessage,
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      let chatId = currentChatId;
      const isNewChat = !chatId;

      if (isNewChat) {
        chatId = await initializeChat();
        if (chatId) {
          handleChatSelect(chatId);
        }
      }

      // Use all messages for display, but limit context for AI
      const allMessages = [...messages, newMessage];
      const contextMessages = allMessages.slice(-100);

      const { data: response, error: generateChatError } = await aiService.generateChat({
        messages: contextMessages,
        tone_of_voice: activeDomain.tone_of_voice || '',
        language: activeDomain.language || '',
        domain_name: activeDomain.domain || '',
        companyId: targetCompany || '',
        customer_group_description:
          customerTargetGroups.find((group) => group.name === selectedCustomerGroup)?.description ||
          '',
      });
      if (generateChatError) {
        throw new Error(await extractErrorMessageFromResponse(generateChatError, t));
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      const reader = response.body.getReader();
      let assistantMessage = '';

      // Add empty assistant message placeholder
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                assistantMessage += data.content;
                setMessages((prev) => [
                  ...prev.slice(0, -1),
                  { role: 'assistant', content: assistantMessage },
                ]);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // After the stream is complete, create the final assistant message
      const finalAssistantMessage: ChatMessage = {
        role: 'assistant',
        content: assistantMessage,
      };

      // Save both messages after we have the complete response
      if (chatId) {
        await saveMessage(chatId, newMessage);
        await saveMessage(chatId, finalAssistantMessage);

        // For new chats, reload messages from the server to ensure consistency
        if (isNewChat) {
          await refreshChats();
          // Reload messages for the new chat to ensure UI is in sync with database
          const chatMessages = await getChatMessages(chatId);
          setMessages(chatMessages);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending message:', error);
      showNotification(
        error instanceof Error ? error.message : 'Error sending message. Please try again.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4" id="chat-container">
        {messages.length === 0 && (
          <div className="text-gray-500 italic text-center mt-10">{t('chat.no_chats')}</div>
        )}
        {messages.map((message, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg group relative ${
              message.role === 'user'
                ? 'bg-blue-100 ml-auto max-w-[80%] mb-2 leading-relaxed'
                : 'bg-gray-100 mr-auto max-w-[80%] mb-2 leading-relaxed'
            }`}
          >
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
            {}
            {message.role === 'assistant' && (
              <button
                onClick={() => navigator.clipboard.writeText(stripMarkdown(message.content))}
                className="absolute top-2 right-2 px-2 py-1 text-sm rounded bg-white shadow-sm hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity text-gray-600 flex items-center gap-1"
                title="Copy to clipboard"
              >
                <FaClipboard className="h-3 w-3" />
                {t('chat.copy')}
              </button>
            )}
          </div>
        ))}
        {isLoading && <div className="text-gray-500 italic">{t('chat.ai_typing')}</div>}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t" id="chat-footer">
        <div className="max-w mx-auto flex gap-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder={t('chat.placeholder')}
            className="flex-1 p-2 border rounded-lg resize-none"
            disabled={isLoading}
            rows={1}
            style={{ overflow: 'hidden', height: 'auto', maxHeight: '250px', overflowY: 'auto' }}
            onInput={(e) => {
              e.currentTarget.style.height = 'auto';
              e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !inputMessage.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {t('chat.send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
